// ADENDA z48-8 (Diego, 28-oct-2026): «ACTUALIZAR A LA VERSIÓN NUEVA». Las reglas que cambian precios nacen PRENDIDAS solo en las
// cotizaciones NUEVAS (motor.parametrosIniciales); una guardada sin ellas no cambia sola. Aquí vive la lista ÚNICA de esos
// parámetros: la banda de la web, la ruta del servidor (en seco y al confirmar) y su prueba contra el motor de verdad la leen de aquí.
import { z } from 'zod';
import { pesos } from '../dinero.ts';
import { CUOTAS_CFE_HASTA_MES, D4_ACTIVA, FACTOR_ELECTRICA_HIBRIDO_AISLADO } from '../constantes.ts';
import { iguales } from '../ediciones.ts';
import { fechaLarga } from '../tiempo.ts';
import type { OpcionId } from './basicos.ts';
import { TARIFAS_CASA } from './basicos.ts';
import type { Cuestionario } from './cuestionario.ts';
import type { ParametrosCalculo, ResultadoMotor, Semaforo } from './opciones.ts';
import { loQueCobraElPdf } from './cotizacion.ts';
import type { Cotizacion, ContenidoCotizacion } from './cotizacion.ts';

export interface ParametroDeVersion {
  clave: keyof ParametrosCalculo;
  /** El valor con que nace en una cotización nueva. */
  valor: unknown;
  /** Desde cuándo nace así (AAAA-MM-DD): «Actualizada a la versión del …». */
  desde: string;
  /** Qué cambia, en palabras del vendedor. */
  texto: string;
  /** En qué cotizaciones nace (la MISMA condición del motor: la prueba del servidor lo compara con el motor y truena si no). */
  aplica: (q: Pick<Cuestionario, 'tipo' | 'respaldo'> & { consumo?: Pick<Cuestionario['consumo'], 'es_nuevo'>; recorte_punta?: Cuestionario['recorte_punta'] }) => boolean;
  /** También nace en la sucursal NUEVA de un multisitio (los demás no: sinParametrosDeVersionDeUnSitio). Una sucursal guardada no
   *  se actualiza sola (parametrosDeVersionQueFaltan no la ofrece en el multisitio). */
  multisitio?: boolean;
  /** En una GUARDADA, ¿cambia algo? (opcional; ausente = sí). Verificación del 28-oct: `negocio_a_pdbt` nace en todo comercio e
   *  industria (el motor decide con el recibo), pero la banda «Actualizar» solo se ofrece donde el recibo es de casa: en un PDBT,
   *  GDMTO o GDMTH guardado el antes/después salía idéntico y era ruido. */
  cambiaEn?: (b: { cuestionario: Pick<Cuestionario, 'tipo' | 'dos_medidores' | 'consumo' | 'tarifa'>; lecturas?: { A?: { tarifa?: string | null } | null } | null }) => boolean;
  /** Nace en las nuevas, pero una guardada NO lo ofrece en la banda «Actualizar» (parametrosDeVersionQueFaltan): `conParametrosDeVersion` aplica
   *  todo lo que falta, y un aumento de precio se iría colado junto con otro cambio. */
  sinBanda?: true;
}

/** Agrega aquí cada parámetro que nazca prendido (motor.ts → parametrosIniciales), con su valor, su fecha y en qué cotizaciones
 *  aplica. El multisitio no los lleva (la sucursal nace sin ellos: cotizaciones.ts → crearCotizacion), salvo los que dicen
 *  `multisitio: true`. */
export const PARAMETROS_DE_VERSION: readonly ParametroDeVersion[] = Object.freeze([
  // Diego, 26-oct (z35-1): casa nueva con la primera opción en el inversor de la Exacta (lista para ampliar).
  { clave: 'inversor_de_exacta', valor: true, desde: '2026-10-26', texto: 'La primera opción lleva el inversor de la Exacta (lista para ampliar)', aplica: (q) => q.tipo === 'casa' },
  // ADENDA z44 (27-oct): casa nueva, siempre 3 opciones.
  { clave: 'tres_opciones_casa', valor: true, desde: '2026-10-27', texto: 'Casa: siempre 3 opciones', aplica: (q) => q.tipo === 'casa' },
  // ADENDA z48-1 (28-oct): híbrido nuevo con baterías para 1 a 2 horas por opción (apagones comunes).
  { clave: 'respaldo_1a2_horas', valor: true, desde: '2026-10-28', texto: 'Híbrido: baterías para 1 a 2 horas en cada opción', aplica: (q) => q.tipo === 'baterias' && q.respaldo?.tipo === 'hibrido' },
  // ADENDA z48-10 (28-oct): cliente nuevo (sin recibos) de casa o negocio: el cuadro de cargas sale mes a mes con la curva de Sonora.
  // ADENDA z46-4 (P18, 28-oct): aislado chico: la Holgada con 8 paneles y el SNA-US 6K (si aguanta la carga).
  { clave: 'aislado_holgada_chico', valor: true, desde: '2026-10-28', texto: 'Aislado chico: la Holgada lleva 8 paneles con el inversor de aislado más barato (SNA-US 6K), si aguanta la carga', aplica: (q) => q.tipo === 'aislado' },
  { clave: 'consumo_estacional', valor: true, desde: '2026-10-28', texto: 'Cliente nuevo: el consumo estimado sale mes a mes con la curva de calor de Sonora (no en dos bloques)', aplica: (q) => consumoEstacionalAplica(q) },
  // ADENDA z46-8 (P20, 28-oct): la generación baja sola con el sol del municipio de la obra (Navojoa, Cajeme…), también en el multisitio.
  { clave: 'sol_del_municipio', valor: true, desde: '2026-10-28', texto: 'La generación baja sola con el sol del municipio de la obra (Navojoa, Cajeme y los que tienen menos sol que Hermosillo)', aplica: (q) => solDelMunicipioAplica(q), multisitio: true },
  // hoy99 (Diego, 2-nov): las cuotas de CFE de noviembre. Solo DAC se movió de verdad (+2.1 %): en las demás el ahorro sale igual (centavos en
  // GDBT y GDMTH), así que la banda no se ofrece y esa cotización se queda como está.
  {
    clave: 'cuotas_cfe_hasta',
    valor: CUOTAS_CFE_HASTA_MES,
    desde: '2026-11-02',
    texto: 'Las cuotas de CFE de noviembre de 2026 en el ahorro',
    aplica: (q) => solDelMunicipioAplica(q),
    multisitio: true,
    cambiaEn: (b) => (b.lecturas?.A?.tarifa ?? b.cuestionario.tarifa) === 'DAC',
  },
  // ADENDA z53-4 (28-oct, listas de octubre): baterías y aislado sin Growatt como principal y con baterías que dan la potencia de la casa.
  { clave: 'respaldo_oct26', valor: true, desde: '2026-10-29', texto: 'Baterías: el equipo principal ya no es Growatt (se puede elegir a mano) y las baterías dan la potencia de la casa', aplica: (q) => respaldoOct26Aplica(q) },
  // ADENDA z52-1 (Diego, 28-oct noche; caso SX-2026-0031): negocio con recibo de casa (1F): con paneles va en tarifa comercial PDBT.
  // Verificación del 28-oct: la banda solo donde cambia algo (recibo de casa, un medidor, con recibo); fecha = la de la regla (z52).
  {
    clave: 'negocio_a_pdbt',
    valor: true,
    desde: '2026-10-28',
    texto: 'Negocio con recibo de casa (1F): al conectar los paneles CFE lo pasa a tarifa comercial PDBT; «con tu sistema» y el ahorro ya lo cuentan',
    aplica: (q) => negocioAPdbtAplica(q),
    cambiaEn: (b) => !b.cuestionario.dos_medidores && !b.cuestionario.consumo.es_nuevo && (TARIFAS_CASA as readonly string[]).includes(b.lecturas?.A?.tarifa ?? ''),
  },
  // ADENDA z54/z55-2 (29-oct): recorte de punta con baterías grandes en GDMTH (método 1). Solo nace con cuestionario.recorte_punta:
  // una industria guardada no lo trae y no cambia.
  { clave: 'recorte_punta', valor: 1, desde: '2026-10-29', texto: 'Recorte de punta con baterías grandes en GDMTH (método del 29-oct: 12 recibos, tope de 0.9 × la punta, ahorro y retorno en rango)', aplica: (q) => recortePuntaAplica(q) },
  // Diego, 2-nov-2026 («toma esta regla para todos los aislados e híbridos siempre»): también en la sucursal nueva de un multisitio.
  { clave: 'electrica_hibrido_aislado', valor: true, desde: '2026-11-02', texto: `Híbrido y aislado: la parte eléctrica se cotiza a ${FACTOR_ELECTRICA_HIBRIDO_AISLADO} veces la de un sistema interconectado (trabajos extras)`, aplica: (q) => electricaHibridoAisladoAplica(q), multisitio: true },
  // Diego, 2-nov-2026 («siempre que el cotizador de entrada dé en verde en todos los proyectos»): la sucursal nueva lo lleva solo si TODAS las del
  // proyecto lo traen (verdeDeEntradaDeLaNueva, servidor): si no, dos sucursales iguales tendrían distinto precio. Sin banda en las guardadas.
  { clave: 'verde_de_entrada', valor: true, desde: '2026-11-02', texto: 'El precio de entrada de cada opción sale en verde; después el vendedor decide si baja a amarillo', aplica: (q) => verdeDeEntradaAplica(q), multisitio: true, sinBanda: true },
]);

/** VERDE DE ENTRADA: en qué cotizaciones nace `verde_de_entrada` (la MISMA condición en motor.parametrosIniciales): todas las que pasan por el motor
 *  solar, recorte de punta incluido (la ampliación de baterías ya divide entre 1 − 30 % − comisión − imprevistos). */
export function verdeDeEntradaAplica(q: Pick<Cuestionario, 'tipo'>): boolean {
  return q.tipo !== 'ampliacion_baterias';
}

/** VERDE DE ENTRADA, decisión 7: el contenido con lo que subió el mínimo en este cálculo, congelado para que una propuesta enviada no suba sola. Lo
 *  que ya estaba congelado manda (la propuesta ya salió con ese precio); un tamaño nuevo se agrega con su subida de hoy. Sin la regla, igual. */
export function conVerdeCongelado<C extends Pick<ContenidoCotizacion, 'parametros' | 'resultado'>>(c: C): C {
  const m = c.resultado?.minimos_verde;
  if (c.parametros.verde_de_entrada !== true || !m) return c;
  const antes = c.parametros.verde_congelado ?? {};
  const nuevo = { ...m, ...antes };
  return iguales(nuevo, antes) ? c : { ...c, parametros: { ...c.parametros, verde_congelado: nuevo } };
}

/** Diego, 2-nov-2026: en qué cotizaciones nace `electrica_hibrido_aislado` (la MISMA condición en motor.parametrosIniciales y en
 *  recalculo.conRespaldoNuevo): todo aislado y todo híbrido. El respaldo (baterías sin paneles) no. */
export function electricaHibridoAisladoAplica(q: Pick<Cuestionario, 'tipo' | 'respaldo'>): boolean {
  return q.tipo === 'aislado' || q.respaldo?.tipo === 'aislado' || q.respaldo?.tipo === 'hibrido';
}

/** z54 (29-oct): en qué cotizaciones nace `recorte_punta` (la MISMA condición en motor.parametrosIniciales). */
export function recortePuntaAplica(q: Pick<Cuestionario, 'tipo'> & { recorte_punta?: Cuestionario['recorte_punta'] }): boolean {
  return q.tipo === 'industria' && !!q.recorte_punta;
}

/** ADENDA z46-8: en qué cotizaciones nace `sol_del_municipio` (la MISMA condición en motor.parametrosIniciales): todas las que pasan
 *  por el motor solar (la ampliación de baterías no tiene paneles ni generación). */
export function solDelMunicipioAplica(q: Pick<Cuestionario, 'tipo'>): boolean {
  return q.tipo !== 'ampliacion_baterias';
}

/** Los parámetros iniciales de una sucursal NUEVA de un multisitio: sin los de versión que no dicen `multisitio` (cada sucursal
 *  con su inversor, sin «siempre 3 opciones»…), con los que sí (el sol del municipio). */
export function sinParametrosDeVersionDeUnSitio<P extends object>(p: P): P {
  return Object.fromEntries(Object.entries(p).filter(([k]) => !PARAMETROS_DE_VERSION.some((x) => x.clave === k && !x.multisitio))) as P;
}

/** ADENDA z53-4: en qué cotizaciones nace `respaldo_oct26` (la MISMA condición en motor.parametrosIniciales, en la banda «Actualizar a
 *  la versión nueva» y en recalculo.conRespaldoNuevo): en NINGUNA mientras la D4 esté apagada (constantes.ts → D4_ACTIVA, 29-oct-2026:
 *  sin la carta de Pylontech/LuxPower, baterías y aislado eligen como producción 7b9adaa); prendida, en baterías y aislado. */
export function respaldoOct26Aplica(q: Pick<Cuestionario, 'tipo'>): boolean {
  return D4_ACTIVA && respaldoOct26DeSuTipo(q);
}
/** Las cotizaciones a las que les tocaría `respaldo_oct26` con la D4 prendida (baterías y aislado), sin mirar el interruptor. */
export function respaldoOct26DeSuTipo(q: Pick<Cuestionario, 'tipo'>): boolean {
  return q.tipo === 'baterias' || q.tipo === 'aislado';
}

/** ADENDA z52-1: en qué cotizaciones nace `negocio_a_pdbt` (la MISMA condición en motor.parametrosIniciales). */
export function negocioAPdbtAplica(q: Pick<Cuestionario, 'tipo'>): boolean {
  return q.tipo === 'comercio' || q.tipo === 'industria';
}

/** ADENDA z48-10: en qué cotizaciones nace `consumo_estacional` (la MISMA condición en motor.parametrosIniciales). */
export function consumoEstacionalAplica(q: Pick<Cuestionario, 'tipo'> & { consumo?: Pick<Cuestionario['consumo'], 'es_nuevo'> }): boolean {
  return q.consumo?.es_nuevo === true && (q.tipo === 'casa' || q.tipo === 'comercio' || q.tipo === 'industria');
}

type ConParametros = { borrador: Pick<ContenidoCotizacion, 'cuestionario' | 'parametros'> & { lecturas?: ContenidoCotizacion['lecturas'] }; multisitio?: unknown };

/** Los parámetros de versión que le FALTAN a una cotización guardada: aplican a su tipo y no los trae (ausente = se hizo antes;
 *  `false` = el vendedor lo apagó a mano y se respeta). El multisitio no los lleva. */
export function parametrosDeVersionQueFaltan(c: ConParametros): ParametroDeVersion[] {
  if (c.multisitio) return [];
  const p = c.borrador.parametros as Record<string, unknown>;
  return PARAMETROS_DE_VERSION.filter((x) => !x.sinBanda && x.aplica(c.borrador.cuestionario) && p[x.clave] === undefined && (!x.cambiaEn || x.cambiaEn(c.borrador)));
}

/** pulido72 (29-oct-2026): ¿dos resultados del motor son la MISMA cotización para el vendedor y el cliente? Se ignora lo que no
 *  cambia lo que se vende ni lo que se cobra: la hora del cálculo, la versión del motor (hoy87), los ids y horas de los avisos, los supuestos escritos (notas
 *  para Diego: no van al PDF) y los avisos «info». Con `generadorDelAislado` (SOLO en un aislado) también lo que el PDF informa
 *  contra el generador de diésel (ADENDA z50): su costo en cada opción y el retorno contra él (`retorno_anios` y el de la
 *  deducción, que en un aislado ya no se calcula). Precio, paneles, inversores, baterías, generación, ahorro, recomendada y
 *  avisos amarillos o rojos cuentan SIEMPRE. Lo usan el PDF (un aislado guardado antes de z50 sale al primer clic) y «Actualizar
 *  a la versión nueva» (la banda solo sale si la versión cambia algo de verdad). */
export function mismoSalvoLoInformativo(a: ResultadoMotor, b: ResultadoMotor, o: { generadorDelAislado?: boolean } = {}): boolean {
  const sinLoInformativo = (r: ResultadoMotor) => ({
    ...r,
    calculado_en: '',
    // hoy87: el número de versión del motor tampoco cambia lo que se vende ni lo que se cobra
    version_motor: '',
    supuestos: [],
    avisos: r.avisos.filter((x) => x.nivel !== 'info').map(({ id: _i, creado_en: _c, ...x }) => x),
    opciones: o.generadorDelAislado
      ? r.opciones.map(({ generador: _g, retorno_anios: _r, fiscal, ...x }) => ({ ...x, fiscal: fiscal ? { ...fiscal, retorno_anios: null } : fiscal }))
      : r.opciones,
  });
  return iguales(sinLoInformativo(a), sinLoInformativo(b));
}

/** hoy97, rondas 3 y 4: los amarillos con los que la casa va en 24 h con LAS CIFRAS DE PRODUCCIÓN (lo fijado o el juego de opciones que ya sacaban su PDF, z45-2).
 *  motor.ts los escribe y truena al cargar si no empiezan como los demás amarillos de la casa. */
export const AVISO_CASA_24_YA_SALIA = 'Aislado sin equipos: toda la casa va en 24 h con las baterías o el equipo que fijaste';
export const AVISO_CASA_24_POR_LAS_OPCIONES = 'Aislado sin equipos: toda la casa va en 24 h con las opciones que dejaste';

/** hoy97, ronda 5 (verificación 1a y 1b): lo que 60d8971 ya imprimió con esos estados se recalcula con las MISMAS cifras y un amarillo nuevo; contando los amarillos como
 *  mismoResultado, el primer PDF daba 409 «cambió algo» sin que cambiara nada. ¿Lo ÚNICO que trae `hoy` de más es ese amarillo (y lo informativo)? Precio, paneles, equipos,
 *  horas, generación, recomendada y cualquier otro amarillo o rojo siguen contando: ahí sigue el 409. */
export function soloElAmarilloDeLaCasaEn24(guardado: ResultadoMotor, hoy: ResultadoMotor): boolean {
  if (guardado.avisos.some(esAmarilloDeLaCasaEn24) || !hoy.avisos.some(esAmarilloDeLaCasaEn24)) return false;
  return mismoSalvoLoInformativo(guardado, { ...hoy, avisos: hoy.avisos.filter((a) => !esAmarilloDeLaCasaEn24(a)) });
}
/** ¿Es uno de esos amarillos? (Es del vendedor: el PDF no lo imprime.) */
export function esAmarilloDeLaCasaEn24(a: { nivel: string; mensaje: string }): boolean {
  return a.nivel === 'amarillo' && (a.mensaje.startsWith(AVISO_CASA_24_YA_SALIA) || a.mensaje.startsWith(AVISO_CASA_24_POR_LAS_OPCIONES));
}

/** Los parámetros con los que faltan, prendidos como nacen hoy. */
export function conParametrosDeVersion<P extends ParametrosCalculo>(p: P, faltan: readonly ParametroDeVersion[]): P {
  return { ...p, ...Object.fromEntries(faltan.map((x) => [x.clave, x.valor])) };
}

/** La fecha de la versión (la del parámetro más nuevo que falta). */
export function fechaDeVersion(faltan: readonly Pick<ParametroDeVersion, 'desde'>[]): string {
  return faltan.reduce((m, x) => (x.desde > m ? x.desde : m), '');
}

export const CuerpoActualizarVersion = z.strictObject({
  revision: z.int().nonnegative(),
  /** false = en seco (no guarda nada); true = guarda, deja el evento y el PDF anterior queda viejo. */
  confirmar: z.boolean(),
});
export type CuerpoActualizarVersion = z.infer<typeof CuerpoActualizarVersion>;

export interface OpcionAntesDespues {
  id: OpcionId;
  paneles: number;
  kwp: number;
  contado: number;
  /** Verificación del 28-oct: con la versión nueva puede cambiar el ahorro y el retorno sin mover paneles ni precio (negocio con
   *  recibo de casa → PDBT): el antes/después los enseña. */
  ahorro_anual: number;
  retorno_anios: number | null;
  /** Solo quien ve el semáforo (Diego y los internos); null para los demás. */
  semaforo: Semaforo | null;
  /** Solo quien ve costos (Diego); null para los demás. */
  margen_pct: number | null;
}
export interface LadoAntesDespues {
  /** La tarifa con que se calculó «con tu sistema» (cambia con negocio_a_pdbt: 1F → PDBT). */
  tarifa: string;
  /** hoy74 (integración de hoy65 con z50): un AISLADO no tiene ahorro de recibo (su retorno es contra el generador de diésel): la
   *  banda no enseña «ahorra $X al año» ahí. */
  aislado: boolean;
  opciones: OpcionAntesDespues[];
  recomendada: OpcionId | null;
  /** Lo que cobra el PDF (la recomendada o, con baterías, el nivel recomendado: loQueCobraElPdf). */
  contado_recomendada: number | null;
  kwp_recomendada: number | null;
}
export interface RespuestaActualizarVersion {
  faltan: { clave: string; texto: string; desde: string }[];
  /** «Actualizar a la versión del …» (AAAA-MM-DD). */
  version_del: string;
  antes: LadoAntesDespues | null;
  despues: LadoAntesDespues | null;
  /** El motor no pudo calcular con la versión nueva: no se guarda nada (al confirmar, 400). */
  error: string | null;
  /** Parte del cambio NO es de la versión: con la tabla de precios vigente ya cambiaba el contado de la recomendada. */
  tambien_tabla: boolean;
  /** pulido72 (29-oct): ¿actualizar CAMBIA algo de esta cotización? Se compara el cálculo de hoy con los parámetros contra el de
   *  hoy sin ellos (mismoSalvoLoInformativo). false = nada (p. ej. el sol del municipio en Hermosillo): la banda no sale y al
   *  confirmar no se guarda nada (el PDF no queda viejo sin motivo). También false si la versión nueva no calcula. */
  cambia: boolean;
  /** Los parámetros que faltan que, cada uno, cambian algo (lo que dice la banda). [] si `cambia` es false; todos los que faltan
   *  si ninguno cambia algo solo y juntos sí. Al confirmar se prenden TODOS los que faltan (`faltan`). */
  con_efecto: { clave: string; texto: string; desde: string }[];
  /** Solo al confirmar: la cotización guardada. */
  cotizacion?: Cotizacion;
}

/** Un lado del antes/después. Lo que no le toca ver a quien pregunta (semáforo, margen) va en null desde aquí. */
export function ladoAntesDespues(b: { cuestionario: Pick<Cuestionario, 'tipo' | 'tarifa'>; resultado: ResultadoMotor | null }, ve: { costos: boolean; semaforo: boolean }): LadoAntesDespues | null {
  const r = b.resultado;
  if (!r) return null;
  const pdf = loQueCobraElPdf(b);
  return {
    tarifa: r.tarifa,
    aislado: b.cuestionario.tipo === 'aislado',
    opciones: r.opciones.map((o) => ({
      id: o.id,
      paneles: o.paneles,
      kwp: o.kwp,
      // hoy87 (verificación): en un AISLADO cada tarjeta es el sistema completo (paneles + inversor aislado + baterías: el precio de su
      // nivel, el que enseña Opciones y cobra el PDF); `o.precio` es SOLO el de los paneles ($71,200 junto a un total de $165,300).
      contado: (b.cuestionario.tipo === 'aislado' ? r.respaldo?.niveles.find((n) => n.opcion === o.id)?.precio?.contado : undefined) ?? o.precio.contado,
      ahorro_anual: o.ahorro_anual,
      retorno_anios: o.retorno_anios,
      semaforo: ve.semaforo ? (o.rentabilidad?.semaforo ?? null) : null,
      margen_pct: ve.costos ? (o.rentabilidad?.margen_contado_pct ?? null) : null,
    })),
    recomendada: r.recomendada ?? null,
    contado_recomendada: pdf.contado,
    kwp_recomendada: pdf.kwp,
  };
}

/** El renglón del historial: «Actualizada a la versión del 27 de octubre de 2026: …. Contado: $A → $B (3 → 3 opciones).». La
 *  fecha es la de TODOS los que se prenden (`faltan`); el texto, el de los que cambian algo (`conEfecto`, pulido72). */
export function textoActualizada(faltan: readonly Pick<ParametroDeVersion, 'texto' | 'desde'>[], antes: LadoAntesDespues | null, despues: LadoAntesDespues | null, conEfecto: readonly Pick<ParametroDeVersion, 'texto'>[] = faltan): string {
  const cifra = (x: number | null | undefined) => (x === null || x === undefined ? 'sin precio' : pesos(x));
  const nOp = (l: LadoAntesDespues | null) => l?.opciones.length ?? 0;
  return `Actualizada a la versión del ${fechaLarga(fechaDeVersion(faltan))}: ${(conEfecto.length ? conEfecto : faltan).map((x) => x.texto).join('; ')}. Contado de lo que cobra el PDF: ${cifra(antes?.contado_recomendada)} → ${cifra(despues?.contado_recomendada)}${nOp(antes) !== nOp(despues) ? ` (${nOp(antes)} → ${nOp(despues)} opciones)` : ''}.${antes && despues && antes.tarifa !== despues.tarifa ? ` Tarifa con paneles: ${antes.tarifa} → ${despues.tarifa}.` : ''} El PDF anterior queda viejo.`;
}

/** hoy87 (ADENDA z62-4, Diego 29-oct): la versión del motor desde la que un AISLADO SIN EQUIPOS cuyas tarjetas no caben con la
 *  casa en 24 h reparte toda la casa en 12 h y saca su PDF. Las cotizaciones guardadas antes llevan el rojo FALTAN_EQUIPOS_AISLADO
 *  («las baterías de la Holgada durarían de 72 a 145 h… Mientras tanto el PDF no sale») GUARDADO: abrirlas no recalcula y la pantalla
 *  apaga «Generar PDF» por ese rojo (verificación de hoy87: solo se destrababa si alguien tocaba «Recalcular» en Opciones, y nada se lo
 *  decía). El motor truena al cargar si su VERSION_MOTOR es menor que esta. */
export const VERSION_MOTOR_AISLADO_12H = '2.9.0';
/** hoy97 (ADENDA z67-4, Diego 30-oct): la versión del motor desde la que «Hacer recomendada» la Arranque en un aislado sin equipos ya no
 *  da el rojo: reparte toda la casa en 12 h y el PDF sale con un amarillo de una línea. Lo que guardó un motor anterior con la Arranque
 *  recomendada lleva el rojo GUARDADO y abrirla no recalcula: la pantalla le ofrece recalcular igual que a lo de antes de las 12 h (y
 *  SOLO a esas: con otra recomendada ese motor ya calculaba lo de hoy). El motor truena al cargar si su VERSION_MOTOR es menor.
 *  Unión del 2-nov: producción publicó la 2.11.0 (parte eléctrica) SIN esta regla. Con 2.10.0 aquí, su rojo contaba como «de hoy» y nada le decía al vendedor
 *  que recalculara: tiene que ser mayor que la versión publicada sin la regla (la 2.10.0 nunca se publicó).
 *  Unión del 2-nov, 19:54 MST: producción publicó la 2.12.0 (verde de entrada) TAMBIÉN sin esta regla: el umbral sube a 2.12.1 por lo mismo. */
export const VERSION_MOTOR_AISLADO_ARRANQUE = '2.12.1';
/** hoy97, verificación (31-oct): el principio del amarillo de UNA línea cuando, con la Arranque recomendada, lo que el vendedor fijó en
 *  Opciones (baterías o par) no cabe en 12 h y toda la casa se queda en 24 h, como producción (motor.ts lo escribe; truena al cargar si
 *  no empieza como los demás amarillos de la casa). La banda de lo guardado por la 2.9.0 lo lee: si el rojo sigue con este amarillo, es
 *  por lo fijado, no porque «ni en 12 h caben». */
export const AVISO_CASA_FIJADO_EN_24 = 'Aislado sin equipos: toda la casa va en 24 h aunque hiciste recomendada la Arranque';
/** Compara dos versiones del motor «M.m.p» (−1, 0, 1); null si alguna no tiene esa forma (el motor simulado de la web: «SIMULADO-0»). */
export function compararVersionMotor(a: string, b: string): -1 | 0 | 1 | null {
  const v = (x: string) => (/^\d+\.\d+\.\d+$/.test(x) ? x.split('.').map(Number) : null);
  const [x, y] = [v(a), v(b)];
  if (!x || !y) return null;
  for (let i = 0; i < 3; i++) if (x[i] !== y[i]) return x[i]! < y[i]! ? -1 : 1;
  return 0;
}
/** ¿Este borrador es un aislado sin equipos con el rojo de baterías que no caben (FALTAN_EQUIPOS_AISLADO con su respaldo calculado)
 *  que calculó un motor ANTERIOR a VERSION_MOTOR_AISLADO_12H? Recalcularlo puede quitar el rojo y sacar el PDF (la pantalla lo
 *  ofrece con su antes y después: BandaRojoDeOtraVersion). Después de recalcular, su versión es la de hoy y ya no se ofrece (si el
 *  rojo sigue, es el de hoy: no cabe ni repartiendo la casa en 12 h). El otro FALTAN_EQUIPOS_AISLADO («sin baterías no funciona»,
 *  sin consumo) no tiene respaldo y no cuenta. */
export function rojoAisladoDeOtraVersion(b: { cuestionario: Pick<Cuestionario, 'tipo' | 'respaldo'>; parametros?: { recomendada?: string | null } | null; resultado: Pick<ResultadoMotor, 'version_motor' | 'respaldo'> | null; avisos: readonly { codigo: string; nivel: string; resuelto?: boolean | null }[] }): boolean {
  if (b.cuestionario.tipo !== 'aislado' || (b.cuestionario.respaldo?.cargas.length ?? 0) > 0) return false;
  if (!b.resultado?.respaldo?.niveles.length) return false;
  const antesDe12h = compararVersionMotor(b.resultado.version_motor, VERSION_MOTOR_AISLADO_12H) === -1;
  // hoy97 (z67-4): la 2.9.0 con la Arranque recomendada guardó el rojo que hoy ya no sale (casa en 12 h + amarillo).
  const antesDeArranque = compararVersionMotor(b.resultado.version_motor, VERSION_MOTOR_AISLADO_ARRANQUE) === -1 && b.parametros?.recomendada === 'arranque';
  if (!antesDe12h && !antesDeArranque) return false;
  return b.avisos.some((a) => a.codigo === 'FALTAN_EQUIPOS_AISLADO' && a.nivel === 'rojo' && !a.resuelto);
}
